// Safety net for Coredna's invisible reCAPTCHA form element.
//
// The framework renders the element as a `.g-recaptcha[data-size="invisible"]`
// div followed by `api.js?onload=<cb>` (async). Google's library auto-renders the
// widget and then calls <cb>, which sets:
//
//   form.onsubmit = function () { grecaptcha.execute(); return false; };
//
// and the widget's data-callback submits the form once a token is issued.
//
// That wiring has no fallback. When the page is re-rendered after a server-side
// validation error (e.g. "email already used"), the recaptcha scripts come from
// browser cache and execute while the POST response is still being parsed. The
// library's startup is "auto-render, then onload callbacks" with nothing in
// between, and on those pages the auto-render never finishes: the widget is left
// half-initialised (`grecaptcha.getResponse()` throws "No reCAPTCHA clients
// exist") and the onload callback is never reached. `form.onsubmit` stays null,
// the next click is a plain POST with no token, and the server answers
// "ReCAPTCHA challenge has to be resolved" without any challenge being shown.
//
// This module waits for the library, gives the framework's callback a moment to
// do its job, and if the form is still unarmed it takes over:
//
//   - renders its own invisible widget (a manual `grecaptcha.render()` works
//     even on the broken pages) whose callback submits the form, and
//   - intercepts the form's submit in the capture phase to execute that widget.
//
// The extra widget's `g-recaptcha-response` textarea is placed after the
// framework's, so when both are posted PHP keeps ours (last one wins).
//
// Plays with the other submit-time modules as follows:
//   form-submit-lock.js  - sees "cancelled, no request, invisible captcha
//                          present" and holds the button until the token
//                          arrives and form.submit() posts. Unchanged.
//   form-phone.js        - blocks an invalid number with
//                          stopImmediatePropagation() at document level, so this
//                          module's listener never runs for that submit. Good.
//   email-lowercase.js   - runs at document capture, before us. Unchanged.

const INVISIBLE_CAPTCHA = '.g-recaptcha[data-size="invisible"]';

/** How long to wait for `grecaptcha.render` to exist before giving up. */
const LIBRARY_TIMEOUT_MS = 15000;
const LIBRARY_POLL_MS = 250;

/**
 * Once the library is loaded, how long to give the framework's own onload
 * callback to arm the form before we conclude it was skipped.
 */
const FRAMEWORK_GRACE_MS = 1500;

/** Marker so the fallback is set up once per form. */
const ARMED_ATTRIBUTE = 'data-captcha-fallback';

function libraryReady() {
	return typeof window.grecaptcha === 'object'
		&& window.grecaptcha !== null
		&& typeof window.grecaptcha.render === 'function';
}

function waitForLibrary() {
	return new Promise((resolve) => {
		const started = Date.now();

		const check = () => {
			if (libraryReady()) {
				resolve(true);
				return;
			}

			if (Date.now() - started >= LIBRARY_TIMEOUT_MS) {
				resolve(false);
				return;
			}

			setTimeout(check, LIBRARY_POLL_MS);
		};

		check();
	});
}

/**
 * Whether the framework's wiring is in place and its auto-rendered widget is
 * usable. Either failing means the submit would go out without a token.
 */
function frameworkArmed(form) {
	if (typeof form.onsubmit !== 'function') {
		return false;
	}

	try {
		// Throws "No reCAPTCHA clients exist." when auto-render never finished.
		window.grecaptcha.getResponse();
		return true;
	} catch (error) {
		return false;
	}
}

function arm(form, captchaElement) {
	if (form.hasAttribute(ARMED_ATTRIBUTE)) {
		return;
	}

	const siteKey = captchaElement.getAttribute('data-sitekey');

	if (!siteKey) {
		return;
	}

	const holder = document.createElement('div');
	holder.className = 'js-captcha-fallback';
	// After the framework's div, so our g-recaptcha-response is the one PHP keeps.
	captchaElement.insertAdjacentElement('afterend', holder);

	let widgetId;

	try {
		widgetId = window.grecaptcha.render(holder, {
			sitekey: siteKey,
			size: 'invisible',
			// Token issued: post the form. form.submit() fires no submit event, so
			// this does not re-enter the listener below. form-submit-lock wraps it
			// so a second call while in flight is swallowed.
			callback: () => form.submit(),
			'error-callback': () => {
				// Network / challenge error: nothing will submit the form, so let the
				// user try again rather than leaving a dead button.
				try {
					window.grecaptcha.reset(widgetId);
				} catch (error) {
					// ignore
				}
			},
		});
	} catch (error) {
		console.warn('FormInvisibleCaptcha: fallback render failed', error);
		holder.remove();
		return;
	}

	form.setAttribute(ARMED_ATTRIBUTE, 'true');

	// Capture phase at the target runs before the form's own onsubmit handler,
	// so even if the framework's callback arms the form later, its
	// `grecaptcha.execute()` against the broken widget never runs.
	form.addEventListener('submit', (event) => {
		event.preventDefault();
		event.stopImmediatePropagation();

		try {
			// A leftover token from an earlier execute on this page is single-use.
			if (window.grecaptcha.getResponse(widgetId)) {
				window.grecaptcha.reset(widgetId);
			}

			window.grecaptcha.execute(widgetId);
		} catch (error) {
			console.warn('FormInvisibleCaptcha: execute failed', error);
		}
	}, true);
}

async function setup() {
	const captchas = Array.from(document.querySelectorAll(INVISIBLE_CAPTCHA));

	if (!captchas.length) {
		return;
	}

	if (!(await waitForLibrary())) {
		return;
	}

	// Let the framework's onload callback run first where it is going to.
	await new Promise((resolve) => setTimeout(resolve, FRAMEWORK_GRACE_MS));

	captchas.forEach((captchaElement) => {
		const form = captchaElement.closest('form');

		if (!form || frameworkArmed(form)) {
			return;
		}

		arm(form, captchaElement);
	});
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => setup());
} else {
	setup();
}
